// Unit placement: a searchable type palette, a click-to-place step, and a
// small confirm form. Plus the shared line-icon set used by the dock.

import * as maplibregl from 'maplibre-gl';
import type { EntityDto, RegistryDto } from './api';
import { get, post } from './api';
import { toast } from './hud';
import { geodesicSector } from './objects';
import { bearingDegBetween, normalizeBearing } from './radar';
import { getActiveStory, isOnlyThisStory } from './story';

type IconName =
  | 'assets' | 'bookmark' | 'live' | 'log' | 'select' | 'deploy'
  | 'area' | 'scan' | 'time' | 'layers' | 'range' | 'shield' | 'chevron'
  | 'intel' | 'user' | 'ruler' | 'burst' | 'plot' | 'markup';

const paths: Record<IconName, string> = {
  assets: '<path d="M5 8.5 12 4l7 4.5v7L12 20l-7-4.5z"/><path d="M12 4v16M5 8.5l7 4.3 7-4.3"/>',
  bookmark: '<path d="M7 4h10v16l-5-3.5L7 20z"/>',
  live: '<circle cx="12" cy="12" r="2"/><path d="M7.8 7.8a6 6 0 0 0 0 8.4M16.2 7.8a6 6 0 0 1 0 8.4M4.5 4.5a10.6 10.6 0 0 0 0 15M19.5 4.5a10.6 10.6 0 0 1 0 15"/>',
  log: '<path d="M6 6h12M6 12h12M6 18h8"/>',
  select: '<circle cx="12" cy="12" r="4"/><path d="M12 2v5M12 17v5M2 12h5M17 12h5"/>',
  deploy: '<path d="M12 3 20 7.5v9L12 21l-8-4.5v-9z"/><path d="M12 8v8M8 12h8"/>',
  area: '<path d="m5 7 7-4 7 5-2 11H7z"/><circle cx="5" cy="7" r="1"/><circle cx="12" cy="3" r="1"/><circle cx="19" cy="8" r="1"/>',
  scan: '<path d="M4 8h5V3M20 8h-5V3M4 16h5v5M20 16h-5v5"/><path d="M8 12h8M12 8v8"/>',
  time: '<circle cx="12" cy="12" r="9"/><path d="M12 7v6l4 2"/>',
  layers: '<path d="m4 8 8-4 8 4-8 4zM4 12l8 4 8-4M4 16l8 4 8-4"/>',
  range: '<circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8"/><path d="M12 1v4M12 19v4M1 12h4M19 12h4"/>',
  shield: '<path d="M12 3 19 6v5c0 5-2.8 8.3-7 10-4.2-1.7-7-5-7-10V6z"/>',
  chevron: '<path d="m8 10 4 4 4-4"/>',
  intel: '<path d="M9 18h6M10 21h4"/><path d="M12 3a6 6 0 0 1 3.6 10.8c-.9.7-1.6 1.3-1.6 2.2h-4c0-.9-.7-1.5-1.6-2.2A6 6 0 0 1 12 3z"/>',
  user: '<circle cx="12" cy="8" r="3.4"/><path d="M5.5 20a6.5 6.5 0 0 1 13 0"/>',
  ruler: '<path d="M3 16.5 16.5 3 21 7.5 7.5 21z"/><path d="M7.5 12.5 9 14M11 9l1.5 1.5M14.5 5.5 16 7"/>',
  burst: '<path d="m12 12 6.5-6.5M12 12l-6.5 6.5M12 12l6.5 6.5M12 12 5.5 5.5M12 12l2-8M12 12l-2 8"/><circle cx="12" cy="12" r="2.2"/>',
  // A drawn shape with ground inside it — hatching is what separates "an
  // area" from the pentagon outline the Places panel already uses.
  plot: '<path d="M4 16 9 5l10 2-1 12z"/><path d="M6.4 13.2 11.2 6.6M8 17.2l7.4-9.6M11.6 18.4 17.6 11"/>',
  // An arrow with a drawn stroke under it: annotation, not a measurement.
  markup: '<path d="M4 20c4-1.5 6.5-4 8-7"/><path d="M7 13.5 19 4l1.5 4.5L9 18z"/><path d="m17.6 5.6 2.2 2.2"/>',
};

export function iconSvg(name: IconName, className = ''): string {
  return `<svg class="${className}" viewBox="0 0 24 24" aria-hidden="true"
    fill="none" stroke="currentColor" stroke-width="1.7"
    stroke-linecap="round" stroke-linejoin="round">${paths[name]}</svg>`;
}

// ---------------------------------------------------------------------------

interface SchemaProperty {
  type?: string;
  default?: unknown;
  enum?: string[];
  title?: string;
}

function propertiesOf(type: RegistryDto['types'][number]): Record<string, SchemaProperty> {
  const schema = type.attrSchema as { properties?: Record<string, SchemaProperty> };
  return schema.properties ?? {};
}

function esc(value: unknown): string {
  return String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function numberDefault(type: RegistryDto['types'][number], key: string): number | null {
  const value = propertiesOf(type)[key]?.default;
  return typeof value === 'number' ? value : null;
}

// A type carries a facing when its schema has azimuth_deg. It's "directional"
// — worth aiming on placement — only when that sector is narrower than a full
// circle (a rotating 360° disc has no meaningful front).
function hasAzimuth(type: RegistryDto['types'][number]): boolean {
  return Boolean(propertiesOf(type).azimuth_deg);
}
function isDirectional(type: RegistryDto['types'][number]): boolean {
  return hasAzimuth(type) && (numberDefault(type, 'azimuth_deg') ?? 360) < 360;
}

function rangeSummary(type: RegistryDto['types'][number]): string {
  const engage = numberDefault(type, 'range_km');
  const radar = numberDefault(type, 'radar_range_km');
  const parts: string[] = [];
  if (engage !== null) parts.push(`${engage} km effective`);
  if (radar !== null) parts.push(`${radar} km radar`);
  return parts.join(' · ');
}

// The one pending click-to-place handler; tracked so tool switches and Escape
// can disarm it (otherwise the NEXT map click would still open the form).
let pendingPlace: ((e: maplibregl.MapMouseEvent) => void) | null = null;
let pendingMap: maplibregl.Map | null = null;
// Teardown for an in-progress aim step, so a tool switch (or a new placement)
// disarms its map handlers and clears the preview wedge.
let activeAimCleanup: (() => void) | null = null;

export function cancelPlacement(): void {
  if (pendingMap && pendingPlace) {
    pendingMap.off('click', pendingPlace);
    pendingMap.getCanvas().classList.remove('placing-asset');
  }
  pendingPlace = null;
  pendingMap = null;
  activeAimCleanup?.();
  document.querySelector('.asset-palette')?.remove();
  document.querySelector('.deploy-confirm')?.remove();
}

function onEscape(fn: () => void): () => void {
  const handler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') fn();
  };
  document.addEventListener('keydown', handler);
  return () => document.removeEventListener('keydown', handler);
}

const AIM_SRC = 'deploy-aim';

function ensureAimLayers(map: maplibregl.Map): void {
  if (map.getSource(AIM_SRC)) return;
  map.addSource(AIM_SRC, { type: 'geojson',
    data: { type: 'FeatureCollection', features: [] } });
  map.addLayer({ id: 'deploy-aim-fill', type: 'fill', source: AIM_SRC,
    paint: { 'fill-color': ['get', 'color'] as never, 'fill-opacity': 0.14 } });
  map.addLayer({ id: 'deploy-aim-line', type: 'line', source: AIM_SRC,
    paint: { 'line-color': ['get', 'color'] as never, 'line-width': 1.6, 'line-opacity': 0.9 } });
}

function removeAimLayers(map: maplibregl.Map): void {
  for (const id of ['deploy-aim-fill', 'deploy-aim-line']) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(AIM_SRC)) map.removeSource(AIM_SRC);
}

// Aim step: a sector-wedge preview follows the cursor from the placed point;
// click locks the facing, Esc keeps the type's default. Then the confirm form
// opens with the chosen bearing.
function beginAim(
  map: maplibregl.Map,
  type: RegistryDto['types'][number],
  coords: [number, number],
  bases: EntityDto[],
  onCreated: (entity: EntityDto) => void,
  onCancel: () => void,
): void {
  const [lon, lat] = coords;
  const color = type.style.color ?? '#4f7fd9';
  const azimuth = numberDefault(type, 'azimuth_deg') ?? 120;
  const previewKm = numberDefault(type, 'radar_range_km')
    ?? numberDefault(type, 'range_km') ?? 40;
  const defaultBearing = numberDefault(type, 'bearing_deg') ?? 0;
  let bearing = defaultBearing;
  ensureAimLayers(map);
  toast('Move to aim the front — click to lock the direction (Esc keeps default)');

  const draw = (brg: number): void => {
    (map.getSource(AIM_SRC) as maplibregl.GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: { color },
        geometry: { type: 'Polygon',
          coordinates: [geodesicSector(lon, lat, previewKm, azimuth, brg)] } }],
    });
  };
  draw(bearing);

  const bearingTo = (e: maplibregl.MapMouseEvent): number =>
    normalizeBearing(bearingDegBetween(lat, lon, e.lngLat.lat, e.lngLat.lng));
  const onMove = (e: maplibregl.MapMouseEvent): void => { bearing = bearingTo(e); draw(bearing); };
  const cleanup = (): void => {
    map.off('mousemove', onMove);
    map.off('click', onClick);
    offAimEscape();
    removeAimLayers(map);
    map.getCanvas().classList.remove('placing-asset');
    activeAimCleanup = null;
  };
  const finish = (locked: boolean): void => {
    cleanup();
    void openDeployForm(type, coords, bases, onCreated, onCancel,
      locked ? bearing : defaultBearing);
  };
  const onClick = (e: maplibregl.MapMouseEvent): void => { bearing = bearingTo(e); finish(true); };
  const offAimEscape = onEscape(() => finish(false));
  activeAimCleanup = cleanup;
  map.on('mousemove', onMove);
  map.on('click', onClick);
}

async function openDeployForm(
  type: RegistryDto['types'][number],
  coords: [number, number],
  bases: EntityDto[],
  onCreated: (entity: EntityDto) => void,
  onCancel: () => void,
  bearing = 0,
): Promise<void> {
  document.querySelector('.deploy-confirm')?.remove();
  const properties = propertiesOf(type);
  const engage = numberDefault(type, 'range_km');
  const radar = numberDefault(type, 'radar_range_km');
  const hasStatus = Boolean(properties.status);
  const dir = hasAzimuth(type);   // show facing/width fields for sectored systems
  const azimuthDefault = numberDefault(type, 'azimuth_deg') ?? 120;
  const form = document.createElement('form');
  form.className = 'pod deploy-confirm';
  form.innerHTML = `
    <header>
      <span class="asset-token" style="--asset:${type.style.color ?? '#4f7fd9'}"></span>
      <span><small>New unit</small><strong>${esc(type.label)}</strong></span>
      <button type="button" class="deploy-x" title="Cancel">×</button>
    </header>
    <div class="deploy-fields">
      <label>Name <span class="deploy-opt">optional</span><input name="name" autofocus
        placeholder="${esc(type.label)}" /></label>
      ${properties.callsign ? '<label>Callsign<input name="callsign" placeholder="Optional" /></label>' : ''}
      ${engage !== null ? `<label>Effective range<div class="range-field">
        <input name="range_km" type="number" min="1" max="500" step=".5" value="${engage}" />
        <span>km</span></div></label>` : ''}
      ${radar !== null ? `<label>Radar range<div class="range-field">
        <input name="radar_range_km" type="number" min="1" max="1000" step=".5" value="${radar}" />
        <span>km</span></div></label>` : ''}
      ${dir ? `<label>Direction<div class="range-field">
        <input name="bearing_deg" type="number" min="0" max="359" step="1" value="${Math.round(bearing)}" />
        <span>°</span></div></label>
      <label>Width<div class="range-field">
        <input name="azimuth_deg" type="number" min="10" max="360" step="5" value="${azimuthDefault}" />
        <span>°</span></div></label>` : ''}
      ${hasStatus ? `<label>Status<select name="status">
        ${(properties.status?.enum ?? ['ready', 'limited', 'offline', 'unknown'])
          .map((value) => `<option value="${esc(value)}"
            ${value === (properties.status?.default ?? 'ready') ? 'selected' : ''}>${esc(value)}</option>`)
          .join('')}</select></label>` : ''}
      <label>Attach to base<select name="base">
        <option value="">None</option>
        ${bases.map((base) => `<option value="${base.id}">${esc(base.name ?? 'Unnamed base')}</option>`)
          .join('')}</select></label>
      <div class="deploy-position"><span>Position</span>
        <b>${coords[1].toFixed(4)}, ${coords[0].toFixed(4)}</b></div>
      <p class="deploy-error" aria-live="polite"></p>
    </div>
    <footer><button type="button" class="secondary">Cancel</button>
      <button type="submit" class="commit">Add unit</button></footer>`;
  document.querySelector('.cockpit')!.appendChild(form);

  const offEscape = onEscape(() => dismiss());
  const dismiss = () => {
    offEscape();
    form.remove();
    onCancel();
  };
  form.querySelector('.deploy-x')!.addEventListener('click', dismiss);
  form.querySelector('.secondary')!.addEventListener('click', dismiss);
  (form.querySelector('[name="name"]') as HTMLInputElement).focus();
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const attrs: Record<string, unknown> = {};
    if (properties.range_km) attrs.range_km = Number(data.get('range_km'));
    if (properties.radar_range_km) attrs.radar_range_km = Number(data.get('radar_range_km'));
    if (properties.status) attrs.status = String(data.get('status') ?? 'ready');
    if (properties.callsign && String(data.get('callsign') ?? '').trim()) {
      attrs.callsign = String(data.get('callsign')).trim();
    }
    if (dir) {
      attrs.bearing_deg = ((Number(data.get('bearing_deg')) % 360) + 360) % 360;
      attrs.azimuth_deg = Number(data.get('azimuth_deg'));
    }
    const submit = form.querySelector('.commit') as HTMLButtonElement;
    const error = form.querySelector('.deploy-error') as HTMLElement;
    submit.disabled = true;
    submit.textContent = 'Adding…';
    try {
      const created = await post<EntityDto>('/api/entities', {
        type: type.key,
        name: String(data.get('name') ?? '').trim() || null,
        attrs,
        geometry: { type: 'Point', coordinates: coords },
      });
      const base = String(data.get('base') ?? '');
      if (base) {
        try {
          await post('/api/edges', {
            src: created.id, dst: base, type: 'located_at',
            attrs: { assigned_at: new Date().toISOString() },
          });
        } catch {
          toast('Unit added, but the base link could not be saved.');
        }
      }
      offEscape();
      form.remove();
      onCreated(created);
    } catch (err) {
      error.textContent = err instanceof Error ? err.message : 'Could not add the unit.';
      submit.disabled = false;
      submit.textContent = 'Add unit';
    }
  });
}

// New-type creator: name a system, "Look up" drafts stats from Wikipedia via
// the server's LLM, the user reviews/edits, Create adds it to the registry.

export const TYPE_ICONS = ['missile', 'gun', 'radar', 'artillery', 'tank', 'apc',
                    'truck', 'ship', 'plane', 'helo', 'uav', 'shield',
                    'dot', 'bookmark', 'nuclear', 'power'];
const TYPE_NUMERIC: [string, string][] = [
  ['range_km', 'Effective range (km)'],
  ['radar_range_km', 'Radar range (km)'],
  ['speed_kmh', 'Speed (km/h)'],
  ['altitude_m', 'Max altitude (m)'],
];

interface TypeProposal {
  label: string;
  category: string;
  icon: string;
  color: string;
  summary: string;
  attrs: Record<string, number>;
  confidence: number;
  sources: { title: string; url: string }[];
}

function typeCreatorView(
  list: HTMLElement,
  registry: RegistryDto,
  onBack: () => void,
  onCreated: (type: RegistryDto['types'][number]) => void,
  prefill?: { query?: string; draft?: Partial<TypeProposal> },
): void {
  const catOptions = registry.categories
    .slice().sort((a, b) => a.sort - b.sort)
    // Objects, actors, and places can all gain new types here; event-domain
    // categories (live events, damage) have dedicated pipelines.
    .filter((c) => c.domain === 'object' || c.domain === 'actor' || c.domain === 'place')
    .map((c) => `<option value="${esc(c.key)}">${esc(c.label)}</option>`).join('');
  list.innerHTML = `
    <form class="type-new">
      <p class="tn-hint">Name a system and <b>Look up</b> drafts its stats from
        Wikipedia. Review, adjust, create — or fill everything in yourself.</p>
      <div class="tn-row">
        <input name="q" placeholder="e.g. David's Sling, Kh-101, THAAD" />
        <button type="button" class="mini" data-t="lookup">Look up</button>
      </div>
      <p class="tn-status" aria-live="polite"></p>
      <div class="tn-fields">
        <label>Label<input name="label" required maxlength="80" /></label>
        <div class="tn-pair">
          <label>Category<select name="category">${catOptions}</select></label>
          <label>Icon<select name="icon">${TYPE_ICONS
            .map((i) => `<option>${i}</option>`).join('')}</select></label>
        </div>
        <div class="tn-pair">
          <label>Colour<input type="color" name="color" value="#4f7fd9" /></label>
          <span></span>
        </div>
        <div class="tn-numeric">${TYPE_NUMERIC.map(([key, label]) => `
        <label>${label}<input name="${key}" type="number" min="0" step="any"
          placeholder="leave blank if n/a" /></label>`).join('')}</div>
        <p class="tn-summary"></p>
        <p class="tn-sources"></p>
      </div>
      <footer class="tn-foot">
        <button type="button" class="mini" data-t="back">Back</button>
        <button type="submit" class="mini tn-commit">Create type</button>
      </footer>
    </form>`;

  const form = list.querySelector('form') as HTMLFormElement;
  const status = form.querySelector('.tn-status') as HTMLElement;
  const field = (name: string) =>
    form.querySelector(`[name="${name}"]`) as HTMLInputElement;

  // Range/radar/speed/altitude are weapon-system stats — irrelevant to a
  // place type (a base, refinery, site), so hide them when a place is chosen.
  const catSel = form.querySelector('[name="category"]') as HTMLSelectElement;
  const numericBox = form.querySelector('.tn-numeric') as HTMLElement;
  const placeCats = new Set(registry.categories
    .filter((c) => c.domain === 'place').map((c) => c.key));
  const syncNumeric = () => { numericBox.hidden = placeCats.has(catSel.value); };
  catSel.addEventListener('change', syncNumeric);
  syncNumeric();

  // One filler for both routes into this form: the Look-up button here, and a
  // draft the equipment lane already researched (its Research button runs the
  // same server-side autofill, so the shape is identical).
  const fillFrom = (p: Partial<TypeProposal>) => {
    if (p.label) field('label').value = p.label;
    if (p.category) {
      (form.querySelector('[name="category"]') as HTMLSelectElement).value = p.category;
    }
    if (p.icon) (form.querySelector('[name="icon"]') as HTMLSelectElement).value = p.icon;
    if (p.color) field('color').value = p.color;
    for (const [key] of TYPE_NUMERIC) {
      field(key).value = p.attrs && key in p.attrs ? String(p.attrs[key]) : '';
    }
    (form.querySelector('.tn-summary') as HTMLElement).textContent = p.summary ?? '';
    (form.querySelector('.tn-sources') as HTMLElement).innerHTML =
      (p.sources ?? []).map((s) => `<a href="${esc(s.url)}" target="_blank"
        rel="noreferrer">${esc(s.title)}</a>`).join(' · ');
    syncNumeric();
    return (p.confidence ?? 1) < 0.5
      ? 'Low confidence — the articles may describe something else. Check the stats.'
      : 'Drafted from Wikipedia — review before creating.';
  };

  form.querySelector('[data-t="back"]')!.addEventListener('click', onBack);
  form.querySelector('[data-t="lookup"]')!.addEventListener('click', async () => {
    const name = field('q').value.trim();
    if (name.length < 2) { status.textContent = 'Type a system name first.'; return; }
    status.textContent = 'Searching Wikipedia…';
    try {
      status.textContent = fillFrom(
        await post<TypeProposal>('/api/registry/types/suggest', { name }));
    } catch (err) {
      status.textContent = err instanceof Error ? err.message : 'Lookup failed.';
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const attrs: Record<string, number> = {};
    for (const [key] of TYPE_NUMERIC) {
      const raw = field(key).value.trim();
      if (raw && Number(raw) > 0) attrs[key] = Number(raw);
    }
    const commit = form.querySelector('.tn-commit') as HTMLButtonElement;
    commit.disabled = true;
    status.textContent = 'Creating…';
    try {
      const created = await post<RegistryDto['types'][number]>('/api/registry/types', {
        label: field('label').value.trim(),
        category: (form.querySelector('[name="category"]') as HTMLSelectElement).value,
        icon: (form.querySelector('[name="icon"]') as HTMLSelectElement).value,
        color: field('color').value,
        attrs,
        summary: (form.querySelector('.tn-summary') as HTMLElement).textContent ?? '',
      });
      toast(`Type created: ${created.label}`);
      onCreated(created);
    } catch (err) {
      status.textContent = err instanceof Error ? err.message : 'Create failed.';
      commit.disabled = false;
    }
  });
  if (prefill?.query) field('q').value = prefill.query;
  if (prefill?.draft) status.textContent = fillFrom(prefill.draft);
  field(prefill?.draft ? 'label' : 'q').focus();
}

/** The type creator on its own, for callers outside the deploy palette — the
 *  equipment advice lane, whose Research button has already drafted the type
 *  and needs the human to review and approve the insert (035's rule: the lane
 *  proposes, a person creates). */
export function openTypeCreator(
  registry: RegistryDto,
  prefill: { query?: string; draft?: Partial<TypeProposal> },
  onCreated: (type: RegistryDto['types'][number]) => void,
): void {
  const panel = document.createElement('section');
  panel.className = 'asset-palette';
  panel.innerHTML = `
    <header><span><small>Registry</small><strong>New type</strong></span>
      <button type="button" class="deploy-x" title="Close">×</button></header>
    <div class="asset-list"></div>`;
  document.querySelector('.cockpit')!.appendChild(panel);
  const close = () => { offEscape(); panel.remove(); };
  const offEscape = onEscape(close);
  panel.querySelector('.deploy-x')!.addEventListener('click', close);
  typeCreatorView(panel.querySelector('.asset-list') as HTMLElement, registry,
                  close, (created) => {
                    registry.types.push(created);
                    onCreated(created);
                    close();
                  }, prefill);
}

export async function openDeployPalette(
  registry: RegistryDto,
  map: maplibregl.Map,
  callbacks: {
    onCreated: (entity: EntityDto) => void;
    onCancel: () => void;
    /** A type was added at runtime — refresh shared registry consumers. */
    onTypeCreated?: (type: RegistryDto['types'][number]) => void;
    /** A polygon-capable place type chosen to be drawn as an area, not a point. */
    onDrawArea?: (type: RegistryDto['types'][number]) => void;
  },
): Promise<void> {
  cancelPlacement();
  const palette = document.createElement('section');
  palette.className = 'asset-palette';
  palette.innerHTML = `
    <header><span><small>Add to map</small><strong>Pick a type</strong></span>
      <button type="button" class="deploy-x" title="Close">×</button></header>
    <div class="asset-search"><input type="search" placeholder="Filter types…" /></div>
    <div class="asset-list"></div>
    <footer><span>Pick a type, then click the map to place it.</span>
      <button type="button" class="mini" data-t="newtype">New type…</button></footer>`;
  document.querySelector('.cockpit')!.appendChild(palette);

  const offEscape = onEscape(() => {
    offEscape();
    cancelPlacement();
    callbacks.onCancel();
  });

  let bases: EntityDto[] = [];
  try {
    // This fetch is independent of panels.refresh(), so the story scope has to
    // be applied here too or the palette offers bases from stories the operator
    // has filtered off the map. The server expands the story's descendants, so
    // the active id alone is the whole subtree. Unfiled bases stay on offer
    // unless "only this story" is on — the same default inScope() uses.
    const story = getActiveStory();
    const scope = story
      ? `&story=${encodeURIComponent(story.id)}${isOnlyThisStory() ? '&unfiled=false' : ''}`
      : '';
    bases = (await get<{ entities: EntityDto[] }>(
      `/api/entities?types=base&limit=200${scope}`)).entities;
  } catch { /* base assignment stays optional */ }

  const input = palette.querySelector('input')!;
  const list = palette.querySelector('.asset-list') as HTMLElement;

  const render = () => {
    // Recomputed every render so a type created moments ago shows at once.
    // Event-domain types (live events, damage) have their own pipelines and
    // never belong in the deploy palette.
    const eventCats = new Set(registry.categories
      .filter((c) => c.domain === 'event').map((c) => c.key));
    const pointTypes = registry.types
      .filter((type) => type.geometryKinds.includes('point')
        && !eventCats.has(type.category))
      .sort((a, b) => a.sort - b.sort);
    const query = input.value.trim().toLowerCase();
    list.innerHTML = '';
    const filtered = pointTypes.filter((type) =>
      `${type.label} ${type.key} ${type.category}`.toLowerCase().includes(query));
    const grouped = new Map<string, typeof pointTypes>();
    for (const type of filtered) {
      const group = grouped.get(type.category) ?? [];
      group.push(type);
      grouped.set(type.category, group);
    }
    const catSort = (key: string) =>
      registry.categories.find((c) => c.key === key)?.sort ?? 99;
    for (const [category, types] of
         [...grouped.entries()].sort((a, b) => catSort(a[0]) - catSort(b[0]))) {
      const heading = document.createElement('div');
      heading.className = 'asset-group';
      heading.textContent = registry.categories.find((c) => c.key === category)?.label ?? category;
      list.appendChild(heading);
      for (const type of types) {
        const summary = rangeSummary(type);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'asset-option';
        button.innerHTML = `
          <span class="asset-token" style="--asset:${type.style.color ?? '#4f7fd9'}"></span>
          <span><b>${esc(type.label)}</b>${summary ? `<small>${esc(summary)}</small>` : ''}</span>`;
        button.addEventListener('click', () => {
          // Polygon-capable place types can be a point OR a drawn area; let the
          // operator choose. Point-only types (units) go straight to placement.
          if (type.geometryKinds.includes('polygon')) choosePlacement(type);
          else beginPointPlace(type);
        });
        list.appendChild(button);
      }
    }
    if (!filtered.length) list.innerHTML = '<div class="asset-empty">No matching types.</div>';
  };
  render();
  input.addEventListener('input', render);

  const searchBox = palette.querySelector('.asset-search') as HTMLElement;
  const backToList = () => {
    searchBox.hidden = false;
    render();
  };

  function beginPointPlace(type: RegistryDto['types'][number]): void {
    offEscape();
    palette.remove();
    toast(`Click the map to place: ${type.label} (Esc cancels)`);
    map.getCanvas().classList.add('placing-asset');
    const offPlaceEscape = onEscape(() => {
      offPlaceEscape();
      cancelPlacement();
      callbacks.onCancel();
    });
    pendingMap = map;
    pendingPlace = (event) => {
      offPlaceEscape();
      pendingPlace = null;
      pendingMap = null;
      const coords: [number, number] = [event.lngLat.lng, event.lngLat.lat];
      // Directional systems get a second "aim the front" step before the form;
      // everything else drops straight to the confirm form.
      if (isDirectional(type)) {
        beginAim(map, type, coords, bases, callbacks.onCreated, callbacks.onCancel);
      } else {
        map.getCanvas().classList.remove('placing-asset');
        void openDeployForm(type, coords, bases, callbacks.onCreated, callbacks.onCancel);
      }
    };
    map.once('click', pendingPlace);
  }

  // Polygon-capable place types: point or drawn area — the operator picks.
  function choosePlacement(type: RegistryDto['types'][number]): void {
    searchBox.hidden = true;
    const color = type.style.color ?? '#4f7fd9';
    list.innerHTML = `
      <div class="asset-choose">
        <p>Add <b>${esc(type.label)}</b> as…</p>
        <button type="button" class="asset-option" data-m="point">
          <span class="asset-token" style="--asset:${color}"></span>
          <span><b>A point</b><small>Drop a single marker</small></span></button>
        <button type="button" class="asset-option" data-m="area">
          <span class="asset-token" style="--asset:${color}"></span>
          <span><b>An area</b><small>Draw a polygon footprint</small></span></button>
      </div>`;
    list.querySelector('[data-m="point"]')!.addEventListener('click',
      () => beginPointPlace(type));
    list.querySelector('[data-m="area"]')!.addEventListener('click', () => {
      offEscape();
      palette.remove();
      callbacks.onDrawArea?.(type);
    });
  }
  palette.querySelector('[data-t="newtype"]')!.addEventListener('click', () => {
    searchBox.hidden = true;
    typeCreatorView(list, registry, backToList, (created) => {
      registry.types.push(created);
      callbacks.onTypeCreated?.(created);
      backToList();
    });
  });

  palette.querySelector('.deploy-x')!.addEventListener('click', () => {
    offEscape();
    palette.remove();
    callbacks.onCancel();
  });
  input.focus();
}
